import { Component, type ErrorInfo, type ReactNode, useEffect, useState } from "react";
import { QueryClientProvider } from "@tanstack/react-query";
import { setSessionExpiredHandler } from "@/lib/apiClient";
import { createQueryClient } from "@/lib/queryClient";
import { useSessionStore } from "@/stores/sessionStore";
import { Button } from "@/components/ui/button";
import { Logo } from "@/components/brand/Logo";
import { UpdateBanner } from "@/features/appVersion/components/UpdateBanner";


interface ErrorBoundaryState {
  error: Error | null;
}

/** ErrorBoundary (RT-10). Sem ele, um erro de render deixa a tela em branco
 *  — e num app usado por pais no celular isso e indistinguivel de "o app
 *  quebrou de vez". */
export class ErrorBoundary extends Component<{ children: ReactNode }, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // Ponto de integracao do error tracking (ADR 0002). Nunca inclua o
    // conteudo da tela no relatorio: pode conter dado de paciente.
    console.error("[ErrorBoundary]", error.message, info.componentStack);
  }

  render(): ReactNode {
    if (!this.state.error) return this.props.children;

    return (
      <div className="flex min-h-dvh flex-col items-center justify-center gap-6 bg-background p-6 text-center">
        <Logo size="lg" />
        <div className="max-w-sm space-y-2">
          <h1 className="text-xl font-semibold">Algo saiu do lugar</h1>
          <p className="text-sm text-muted-foreground">
            Tivemos um problema ao carregar esta tela. Seus registros estao salvos.
          </p>
        </div>
        <Button onClick={() => window.location.reload()}>Recarregar</Button>
      </div>
    );
  }
}

/** Liga o apiClient ao store: quando o refresh falha, a sessao e limpa e o
 *  router leva ao login. Sem isso, a UI ficaria mostrando um usuario que o
 *  servidor ja nao reconhece. */
function SessionExpiryBridge({ children }: { children: ReactNode }) {
  const clearSession = useSessionStore((s) => s.clearSession);

  useEffect(() => {
    setSessionExpiredHandler(() => clearSession());
    return () => setSessionExpiredHandler(() => {});
  }, [clearSession]);

  return <>{children}</>;
}

export function AppProviders({ children }: { children: ReactNode }) {
  // Criado uma vez por montagem: um QueryClient recriado a cada render
  // descartaria o cache inteiro em toda atualizacao de estado.
  const [queryClient] = useState(createQueryClient);

  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <SessionExpiryBridge>
          <UpdateBanner />
          {children}
        </SessionExpiryBridge>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}
